import React, { useState, useEffect } from "react";
import { Button } from "primereact/button";
import { useFormik } from "formik";
import formService from "../../services/forms/form.service";
import { AnketRequestDto } from "../../services/forms/form.dto";
import { useToast } from "../../utils/toast";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Checkbox, CheckboxChangeEvent } from "primereact/checkbox";
import ProtokolFirmalarComponent from "../../components/ProtokolFirmalarComponent";
import adminService from "../../services/admin/admin.services";
import { ReportDto } from "../../services/admin/admin.dto";
import LoginRequiredModal from "../../components/LoginRequiredModal";
import { useAuth } from "../../utils/auth";

const Raporlama = () => {
  const toast = useToast();
  const auth = useAuth();
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [reports, setReports] = useState<ReportDto[]>([]);

  useEffect(() => {
    fetchReports();
  }, []);

  const fetchReports = async () => {
    try {
      const response = await adminService.getReports();
      if (response?.result?.data) {
        setReports(response.result.data);
      }
    } catch (error) {
      console.error("Error fetching reports:", error);
      toast.show("Raporlar yüklenirken bir hata oluştu.", "error");
    }
  };

  const downloadBodyTemplate = (report: ReportDto) => {
    return (
      <Button
        label="İndir"
        icon="pi pi-download"
        onClick={async () => {
          try {
            const response = await adminService.downloadReport(report.id!);

            if (!response || !response.result) {
              throw new Error("Download failed");
            }

            // Assert response.result as unknown, then as Blob
            const blob = response.result as unknown as Blob;

            // Try to get the filename from the Content-Disposition header
            const contentDisposition = response.headers?.["content-disposition"];
            let filename = report.title;
            if (contentDisposition) {
              const filenameMatch = contentDisposition.match(/filename="?(.+)"?/i);
              if (filenameMatch) {
                filename = filenameMatch[1];
              }
            } else {
              // If no Content-Disposition header, get the extension from the link URL
              const extension = report.link.split(".").pop();
              filename = `${report.title}.${extension}`;
            }

            // Create object URL from the blob
            const url = window.URL.createObjectURL(blob);

            // Create temporary link and trigger download
            const link = document.createElement("a");
            link.href = url;
            link.download = filename;
            document.body.appendChild(link);
            link.click();

            // Cleanup
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);
          } catch (error) {
            console.error("Download error:", error);
            toast.show("Dosya indirilirken bir hata oluştu.", "error");
          }
        }}
      />
    );
  };

  const formik = useFormik<AnketRequestDto>({
    initialValues: {
      result1: ""
    },
    validate: (values) => {
      const errors: any = {};
      if (!values.result1 || values.result1 === "") {
        errors.result1 = "Bir seçenek şeçiniz.";
      }
      return errors;
    },
    onSubmit: async (values) => {
      // Kullanıcının giriş yapıp yapmadığını kontrol et
      if (!auth.getUser().access_token) {
        setShowLoginModal(true);
        return;
      }

      formService.raporForm(values).then((res) => {
        if (res?.result?.error === null) {
          toast.show("Anket kaydedildi.", "success");
        } else {
          toast.show("Anket kaydedilemedi", "error");
        }
      });
    }
  });

  const options = [
    { label: "Karbon ayak izi", value: "karbon_ayak_izi" },
    { label: "Su ayak izi", value: "su_ayak_izi" },
    { label: "Ürün karbon ayak izi", value: "urun_karbon_ayak" },
    { label: "Ekolojik ayak izi", value: "ekolojik_ayak_izi" }
  ];

  const handleCheckboxChange = (e: CheckboxChangeEvent) => {
    // Kullanıcının giriş yapıp yapmadığını kontrol et
    if (!auth.getUser().access_token) {
      setShowLoginModal(true);
      return;
    }

    const value = e.value;
    let newAnalysisAreas: string = "";

    if (e.checked) {
      newAnalysisAreas = value;
    }

    formik.setFieldValue("result1", newAnalysisAreas);
  };

  const footer = `Toplam ${reports ? reports.length : 0} rapor bulunmakta.`;

  return (
    <>
      <div className="card">
        <div className="text-center mb-4">
          <h2 className="text-900 text-2xl sm:text-3xl font-semibold mb-2">Raporlar</h2>
        </div>
        <div className="w-full table-responsive-container">
          <DataTable
            value={reports}
            showGridlines
            footer={footer}
            rows={5}
            paginator
            rowsPerPageOptions={[5, 10, 25, 50]}
            scrollable
            columnResizeMode="expand"
            emptyMessage="Rapor bulunamadı."
          >
            <Column field="title" header="Rapor Adı" style={{ minWidth: "12rem" }}></Column>
            <Column field="date" header="Tarih" style={{ minWidth: "8rem" }}></Column>
            <Column field="category" header="Rapor Tipi" style={{ minWidth: "8rem" }}></Column>
            <Column header="İndirme" body={downloadBodyTemplate} style={{ minWidth: "6rem" }}></Column>
          </DataTable>
        </div>
      </div>

      <div className="card">
        <div className="w-full surface-card">
          <div className="text-center mb-4">
            <h2 className="text-900 text-2xl sm:text-3xl font-semibold mb-2">Raporlama Anketi</h2>
          </div>

          <form onSubmit={formik.handleSubmit}>
            <div className="flex flex-column align-items-start">
              <p className="text-900 font-medium mb-3 line-height-3">
                OSB bünyesinde etkili kararlar alabilmek için ihtiyaç duyduğunuz analiz alanları aşağıdakilerden
                hangisidir?
              </p>
              <div className="grid w-full">
                {options.map((option) => (
                  <div key={option.value} className="col-12 sm:col-6 flex align-items-center mb-2">
                    <Checkbox
                      inputId={option.value}
                      value={option.value}
                      onChange={handleCheckboxChange}
                      checked={formik.values.result1.includes(option.value)}
                    />
                    <label htmlFor={option.value} className="ml-2 cursor-pointer text-sm sm:text-base">
                      {option.label}
                    </label>
                  </div>
                ))}
              </div>

              {formik.errors.result1 && <small className="p-error block mt-1">{formik.errors.result1}</small>}
              <Button label="Gönder" type="submit" icon="pi pi-check" className="mt-3 px-4" />
            </div>
          </form>
        </div>
      </div>

      <ProtokolFirmalarComponent />
      
      {/* Login Required Modal */}
      <LoginRequiredModal 
        visible={showLoginModal} 
        onHide={() => setShowLoginModal(false)} 
      />
    </>
  );
};

export default Raporlama;
